import { useState, useEffect } from "react";
import { Modal } from ".";
import Spinner from "../Spinner/Spinner";
import { CloseIcon } from "../../../icons";
import { Wallet as WalletType } from "../../wallet/types";
import { fetchWallets } from "../../wallet/API/services";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";
import FundWalletModal from "../../wallet/modals/FundModal";
import CreateWalletModal from "../../wallet/modals/CreateWalletModal"; // Import the create modal

interface Wallet {
  id: string;
  currency: string;
  balance: number;
  walletName: string;
}

interface WalletSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (wallet: Wallet) => void;
  currency: string;
  amount?: number;
}

export function WalletSelectionModal({
  isOpen,
  onClose,
  onSelect,
  currency,
  amount = 0,
}: WalletSelectionModalProps) {
  const [wallets, setWallets] = useState<WalletType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [insufficientBalanceWallets, setInsufficientBalanceWallets] = useState<
    string[]
  >([]);
  const [fundModalOpen, setFundModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false); // New state for create modal
  const [selectedWalletForFunding, setSelectedWalletForFunding] =
    useState<WalletType | null>(null);

  const { loadCurrencies, getCurrencyByCode, hasLoaded } = useCurrencyStore();

  // Load currencies if not loaded
  useEffect(() => {
    if (isOpen && !hasLoaded) {
      loadCurrencies();
    }
  }, [isOpen, hasLoaded, loadCurrencies]);

  const currentCurrency = getCurrencyByCode(currency);
  const supportsWalletCreation = currentCurrency?.allowWalletCreation ?? false;
  const hasExistingWallet = wallets.length > 0;

  // Fetch wallets function that can be called from multiple places
  const fetchUserWallets = async () => {
    try {
      setLoading(true);
      setError("");
      setWallets([]);
      setInsufficientBalanceWallets([]);

      // Fetch wallets from API
      const data: any = await fetchWallets();
      const filteredWallets = data.filter(
        (wallet: WalletType) => wallet.currency === currency
      );

      setWallets(filteredWallets);

      // Check for insufficient balance
      if (amount > 0) {
        const insufficientIds = filteredWallets
          .filter((wallet: WalletType) => wallet.balance < amount)
          .map((wallet: WalletType) => wallet.id);
        setInsufficientBalanceWallets(insufficientIds);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load wallets");
      console.error("Wallet fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserWallets();
    }
  }, [isOpen, currency, amount]);

  const handleWalletSelect = (wallet: WalletType) => {
    // Check if wallet has sufficient balance
    if (amount > 0 && wallet.balance < amount) {
      // Instead of alert, show fund modal
      setSelectedWalletForFunding(wallet);
      setFundModalOpen(true);
      return;
    }

    // Convert WalletType to the expected Wallet interface
    const selectedWallet: Wallet = {
      id: wallet.id,
      currency: wallet.currency,
      balance: wallet.balance,
      walletName: wallet.name,
    };

    onSelect(selectedWallet);
  };

  const handleFundSuccess = () => {
    // Refresh wallets after successful funding
    fetchUserWallets();
  };

  const handleCreateWalletSuccess = () => {
    // Refresh wallets after successful creation
    fetchUserWallets();
  };

  const handleCreateWalletClick = () => {
    if (!supportsWalletCreation) {
      alert(
        `This currency (${currency}) does not support wallet creation at the moment.`
      );
      return;
    }
    // Open create modal instead of redirecting
    setCreateModalOpen(true);
  };

  const handleFundWalletClick = (wallet: WalletType) => {
    setSelectedWalletForFunding(wallet);
    setFundModalOpen(true);
  };

  const formatWalletBalance = (wallet: WalletType) => {
    const hasInsufficientBalance = insufficientBalanceWallets.includes(
      wallet.id
    );

    return (
      <div className="flex flex-col">
        <div className="font-medium text-gray-800 dark:text-white">
          {wallet.name} Wallet
        </div>
        <div className="flex justify-between items-center">
          <div
            className={`text-sm ${
              hasInsufficientBalance
                ? "text-red-500"
                : "text-gray-600 dark:text-gray-300"
            }`}
          >
            Balance: {wallet.symbol}
            {wallet.balance.toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Wallet Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
              Select Wallet ({currency})
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          {amount > 0 && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <span className="font-medium">Exchange Amount: </span>
                {amount.toLocaleString()} {currency}
              </div>
            </div>
          )}

          {!supportsWalletCreation && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <div className="text-sm text-amber-700 dark:text-amber-300">
                <span className="font-medium">Note:</span> This currency (
                {currency}) does not support wallet creation.
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8 text-blue-500" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">
              <div className="mb-2">{error}</div>
              <button
                onClick={() => fetchUserWallets()}
                className="text-sm text-blue-500 hover:text-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : wallets.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400 mb-6">
                {supportsWalletCreation
                  ? `No ${currency} wallet found. Create one to continue.`
                  : ``}
              </div>
              {supportsWalletCreation ? (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleCreateWalletClick}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Create {currency} Wallet
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-sm text-gray-400 mb-3">
                    Please select a different currency
                  </div>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Go Back
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {wallets.map((wallet) => {
                  const hasInsufficientBalance =
                    insufficientBalanceWallets.includes(wallet.id);
                  const isDisabled = amount > 0 && hasInsufficientBalance;

                  return (
                    <div
                      key={wallet.id}
                      className={`p-4 border rounded-lg transition-all ${
                        isDisabled
                          ? "border-red-300 bg-red-50 dark:bg-red-900/20 opacity-80"
                          : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      } dark:border-gray-700`}
                      onClick={() => !isDisabled && handleWalletSelect(wallet)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {wallet.flag && (
                            <img
                              src={wallet.flag}
                              alt={wallet.currency}
                              className="h-8 w-8 rounded-full"
                            />
                          )}
                          <div className="flex-1">
                            {formatWalletBalance(wallet)}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {isDisabled && amount > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFundWalletClick(wallet);
                              }}
                              className="text-xs px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                            >
                              Fund Wallet
                            </button>
                          )}
                        </div>
                      </div>

                      {isDisabled && hasInsufficientBalance && (
                        <div className="mt-3 flex items-center gap-2 rounded-md bg-red-100 dark:bg-red-900/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                          <span className="font-medium">
                            Insufficient balance
                          </span>
                          <span className="opacity-70">
                            — fund this wallet to continue
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {supportsWalletCreation && !hasExistingWallet && (
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleCreateWalletClick}
                    className="w-full py-3 text-center text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    + Create New {currency} Wallet
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Fund Wallet Modal */}
      {selectedWalletForFunding && (
        <FundWalletModal
          isOpen={fundModalOpen}
          onClose={() => {
            setFundModalOpen(false);
            setSelectedWalletForFunding(null);
          }}
          wallet={selectedWalletForFunding}
          onSuccess={handleFundSuccess}
        />
      )}

      {/* Create Wallet Modal */}
      <CreateWalletModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
        }}
        onCreated={() => {
          handleCreateWalletSuccess();
          setCreateModalOpen(false);
        }}
        existingWallets={wallets}
        preSelectedCurrencyCode={currency}
      />
    </>
  );
};